import { compactNumber as compactNumberFunc } from '@/utilities/Number';
import { Truncate } from './Helpers';
import { percentage as percentageFunc } from './Number';
import { compactMoney as compactMoneyFunc } from './Money';
import { DateTime } from 'luxon';
import { safeHighlight } from './Sanitization';

export const filters = {
  highlight,
  datetime,
  date,
  relative,
  dashempty,
  spaceempty,
  dashblank,
  shorterdate,
  money,
  percentage,
  compactMoney,
  compactNumber,
  number,
  integer,
  bool,
  truncate,
} as const;

function highlight(text: string, query: string): string {
  // Use the safe highlight function to prevent XSS attacks
  return safeHighlight(text, query);
}

function datetime(value: string | DateTime | Date | undefined, localeOptions = DateTime.DATETIME_FULL) {
  if (!value) return '';
  let dt = value;
  if (typeof value === 'string') {
    dt = DateTime.fromISO(value);
  } else if (value instanceof Date) {
    dt = DateTime.fromJSDate(value);
  }
  return dt.toLocaleString(localeOptions);
}

function shorterdate(value: string | DateTime | Date | undefined) {
  if (!value) return '';
  let dt = value;
  if (typeof value === 'string') {
    dt = DateTime.fromISO(value, { zone: 'utc' });
  } else if (value instanceof Date) {
    dt = DateTime.fromJSDate(value);
  }
  return (dt as DateTime).toFormat('L/d/yy');
}

function date(value: string | DateTime | Date | undefined) {
  if (!value) return '';
  let dt = value;
  if (typeof value === 'string') {
    dt = DateTime.fromISO(value, { zone: 'utc' });
  } else if (value instanceof Date) {
    dt = DateTime.fromJSDate(value);
  }
  return dt.toLocaleString(DateTime.DATE_SHORT);
}

function relative(value: string | DateTime | Date | undefined) {
  if (!value) return '';
  let dt: DateTime | undefined;
  if (typeof value === 'string') {
    dt = DateTime.fromISO(value, { zone: 'utc' });
  } else if (value instanceof Date) {
    dt = DateTime.fromJSDate(value);
  }
  if (dt) {
    return dt.toRelative();
  }
  return '';
}

function dashempty(value: unknown) {
  return value || '--';
}

function spaceempty(value: unknown) {
  return value || '\xa0';
}

function dashblank(value: unknown) {
  return !value ? '--' : value;
}

function money(value: number | undefined | null, min = 2, max = 2): string {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
  }
  return '';
}

function percentage(
  value: number | undefined | null,
  factor = 100,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1,
): string | undefined {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return percentageFunc(value * factor, minimumFractionDigits, maximumFractionDigits);
  }
  return '';
}

function compactMoney(value: number | undefined | null, levels = ['T', 'B', 'M', 'K']): string {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return compactMoneyFunc(value, levels);
  }
  return '';
}

export function compactNumber(value: number | undefined | null, compactDecimal = false): string {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return compactNumberFunc(value, undefined, undefined, compactDecimal);
  }
  return '';
}

function number(value: number | undefined | null, min = 1, max = 1): string {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return value.toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });
  }
  return '';
}

function integer(value: number | undefined | null, min = 0, max = 0): string {
  if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
    return value.toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });
  }
  return '';
}

function bool(value: never | undefined | null | boolean): string {
  return value ? 'Yes' : '--';
}

function truncate(value: string | undefined | null, max = 30): string {
  return Truncate('' + value, max);
}
